/**
 * <carousel-slider>: one carousel for every slider and rail in the theme.
 *
 * Built on native horizontal scrolling with CSS scroll snap, so touch swipe,
 * trackpads and keyboard scrolling work without JavaScript. This script adds:
 * arrows, dots with autoplay progress, mouse drag, arrow keys (RTL aware),
 * autoplay with pause/play, and theme-editor block selection.
 *
 * Markup contract:
 *   <carousel-slider [data-autoplay] [data-interval="6000"] [data-loop]>
 *     <div data-carousel-track> <div data-carousel-slide>…</div> … </div>
 *     [data-carousel-prev] [data-carousel-next] [data-carousel-toggle]
 *     [data-carousel-dot="0"] … [data-carousel-status]
 *   </carousel-slider>
 */
import { reducedMotion } from '@theme/core';

class CarouselSlider extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #held = false;
  #drag = null;
  #observer;

  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.toggle = this.querySelector('[data-carousel-toggle]');
    this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
    this.status = this.querySelector('[data-carousel-status]');
    this.autoplay = this.hasAttribute('data-autoplay');
    this.loop = this.hasAttribute('data-loop') || this.autoplay;

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.carouselDot))));
    this.toggle?.addEventListener('click', () => this.#setPaused(!this.#paused));

    this.track.addEventListener('keydown', (event) => this.#onKeydown(event));
    this.track.addEventListener('scroll', () => this.#onScroll(), { passive: true });
    this.#setupDrag();

    // Pause autoplay while the visitor interacts or the carousel is off screen
    this.addEventListener('pointerenter', () => this.#hold(true));
    this.addEventListener('pointerleave', () => this.#hold(false));
    this.addEventListener('focusin', () => this.#hold(true));
    this.addEventListener('focusout', (event) => !this.contains(event.relatedTarget) && this.#hold(false));
    this.#observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.#schedule();
    });
    this.#observer.observe(this);

    // Theme editor: selecting a slide block jumps to it and stops autoplay
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.slides.findIndex((slide) => slide.contains(event.target));
      if (index > -1) this.goTo(index, { instant: true });
      this.#hold(true);
    });
    this.addEventListener('shopify:block:deselect', () => this.#hold(false));

    if (this.autoplay && reducedMotion()) this.#setPaused(true);
    this.#update();
    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#observer?.disconnect();
  }

  get slides() {
    return [...this.track.querySelectorAll(':scope > [data-carousel-slide]')].filter(
      (slide) => slide.offsetParent !== null || slide.getClientRects().length,
    );
  }

  get isRTL() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  /** Distance from the track's start edge to the slide's start edge. */
  #offsetOf(slide) {
    const track = this.track.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
    return this.isRTL
      ? this.track.scrollLeft + (rect.right - track.right) + padding
      : this.track.scrollLeft + (rect.left - track.left) - padding;
  }

  /** How many slides fit in the viewport (rails show several). */
  get perView() {
    const first = this.slides[0];
    if (!first) return 1;
    return Math.max(1, Math.round(this.track.clientWidth / first.getBoundingClientRect().width));
  }

  get maxIndex() {
    return Math.max(0, this.slides.length - this.perView);
  }

  goTo(index, { instant = false } = {}) {
    const slides = this.slides;
    if (!slides.length) return;
    const max = this.maxIndex;
    let target = index;
    if (this.loop) {
      if (target > max) target = 0;
      if (target < 0) target = max;
    }
    target = Math.min(Math.max(target, 0), max);

    this.track.scrollTo({
      left: this.#offsetOf(slides[target]),
      behavior: instant || reducedMotion() ? 'instant' : 'smooth',
    });
    this.#index = target;
    this.#update();
    this.#schedule();
  }

  step(direction) {
    // Rails move by a page of visible slides; single slides move by one
    const amount = this.perView > 1 ? this.perView : 1;
    this.goTo(this.#index + direction * amount);
  }

  #onScroll() {
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(() => {
      const slides = this.slides;
      const scroll = Math.abs(this.track.scrollLeft);
      let closest = 0;
      let best = Infinity;
      slides.forEach((slide, i) => {
        const distance = Math.abs(Math.abs(this.#offsetOf(slide)) - scroll);
        if (distance < best) {
          best = distance;
          closest = i;
        }
      });
      // At the far end, the last page is active even if it can't snap to start
      const atEnd = scroll + this.track.clientWidth >= this.track.scrollWidth - 4;
      this.#index = atEnd ? this.maxIndex : closest;
      this.#update();
    });
  }

  #update() {
    const slides = this.slides;
    const index = this.#index;
    slides.forEach((slide, i) => {
      const inView = i >= index && i < index + this.perView;
      slide.toggleAttribute('data-active', i === index);
      // Keep off-screen slide content out of the tab order in slide mode
      if (this.perView === 1) slide.inert = !inView;
    });

    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.toggleAttribute('data-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (!this.loop) {
      if (this.prev) this.prev.disabled = index <= 0;
      if (this.next) this.next.disabled = index >= this.maxIndex;
    }

    const scrollable = this.track.scrollWidth > this.track.clientWidth + 4;
    this.toggleAttribute('data-scrollable', scrollable);

    if (this.status && this.perView === 1 && slides.length) {
      this.status.textContent = this.status.dataset.template
        ?.replace('[current]', index + 1)
        .replace('[total]', slides.length);
    }
  }

  #onKeydown(event) {
    if (event.target !== this.track) return;
    const forward = this.isRTL ? 'ArrowLeft' : 'ArrowRight';
    const back = this.isRTL ? 'ArrowRight' : 'ArrowLeft';
    if (event.key === forward) {
      event.preventDefault();
      this.step(1);
    } else if (event.key === back) {
      event.preventDefault();
      this.step(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.goTo(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.goTo(this.maxIndex);
    }
  }

  /** Mouse drag (touch already scrolls natively). */
  #setupDrag() {
    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.#drag = { x: event.clientX, scroll: this.track.scrollLeft, moved: false };
    });

    window.addEventListener('pointermove', (event) => {
      if (!this.#drag) return;
      const delta = event.clientX - this.#drag.x;
      if (!this.#drag.moved && Math.abs(delta) < 6) return;
      if (!this.#drag.moved) {
        this.#drag.moved = true;
        this.setAttribute('data-dragging', '');
      }
      this.track.scrollLeft = this.#drag.scroll - delta;
    });

    window.addEventListener('pointerup', () => {
      if (!this.#drag) return;
      const { moved } = this.#drag;
      this.#drag = null;
      if (!moved) return;
      this.removeAttribute('data-dragging');
      // Swallow the click that ends a drag so links inside slides don't fire
      this.track.addEventListener('click', (e) => e.preventDefault(), { capture: true, once: true });
      setTimeout(() => this.goTo(this.#index), 30);
    });

    this.track.addEventListener('dragstart', (event) => event.preventDefault());
  }

  #setPaused(paused) {
    this.#paused = paused;
    this.toggle?.setAttribute('aria-pressed', String(paused));
    const label = this.toggle?.querySelector('[data-carousel-toggle-label]');
    if (label) label.textContent = paused ? label.dataset.play : label.dataset.pause;
    this.toggleAttribute('data-paused', paused);
    this.#schedule();
  }

  #hold(state) {
    this.#held = state;
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    const running = this.autoplay && !this.#paused && !this.#held && this.visible !== false && !document.hidden;
    this.toggleAttribute('data-running', running);
    if (!running) return;
    this.#timer = setTimeout(() => this.step(1), Number(this.dataset.interval) || 6000);
  }
}

if (!customElements.get('carousel-slider')) customElements.define('carousel-slider', CarouselSlider);

document.addEventListener('visibilitychange', () => {
  document.querySelectorAll('carousel-slider').forEach((el) => el.dispatchEvent(new Event('pointerleave')));
});
